import { useUserActions } from '@repo/domain-service';
import { TestId } from '@repo/test-id';
import { Modal } from '@repo/ui';
import { useEffect } from 'react';

import { useAuthFormStore } from '@features/auth/stores/useAuthFormStore';
import AuthConversionConflictModal from '@features/auth/components/AuthConversionConflictModal';
import EmailSection from '@features/auth/components/EmailSection';
import CodeSection from '@features/auth/components/CodeSection';
import PasswordSection from '@features/auth/components/PasswordSection';
import ConsentsSection from '@features/auth/components/ConsentsSection';
import GuestSection from '@features/auth/components/GuestSection';
import { useAuthModal, type AuthModalRegistrationData } from '@features/auth/hooks/useAuthModal';

interface Props {
  onOpenChange?: (open: boolean) => void;
  email?: string;
  registrationData?: AuthModalRegistrationData;
  allowGuest?: boolean;
}

export default function AuthModal({ onOpenChange, email: initialEmail, registrationData, allowGuest }: Props) {
  const handleClose = () => onOpenChange?.(false);
  const testId = TestId.root('authDrawer');
  const {
    requesting,
    validating,
    validatingPassword,
    finalizing,
    requestingPasswordReset,
    registeringAsGuest,
    convertingTemporaryUser,
  } = useUserActions();
  const email = useAuthFormStore((state) => state.email);
  const setEmail = useAuthFormStore((state) => state.setEmail);

  const {
    step,
    expectedConsents,
    resendSecondsLeft,
    submitEmail,
    confirmCode,
    confirmPassword,
    requestPasswordReset,
    resendCode,
    changeEmail,
    continueAsGuest,
    acceptConsents,
    conversionConflictOpen,
    confirmConversionConflict,
    cancelConversionConflict,
  } = useAuthModal(registrationData, initialEmail);

  const emailRequesting = requesting || convertingTemporaryUser;

  const handleCancelConflict = () => {
    cancelConversionConflict();
    onOpenChange?.(false);
  };

  const handleConfirmConflict = () => {
    confirmConversionConflict().catch(() => {});
  };

  useEffect(() => {
    if (initialEmail) {
      setEmail(initialEmail);
      submitEmail(initialEmail).catch(() => {});
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <>
      <Modal open testId={testId} onOpenChange={onOpenChange} dismissableOnClickOutside={false}>
        <div className="flex flex-col gap-4 py-4">
          {step === 'email' && (
            <>
              <EmailSection
                testId={testId}
                email={email}
                setEmail={setEmail}
                submitEmail={submitEmail}
                requesting={emailRequesting}
                initialEmail={initialEmail}
                onClose={handleClose}
              />
              {allowGuest && (
                <>
                  <div className="border-t border-gray-200" />
                  <GuestSection testId={testId} onContinueAsGuest={continueAsGuest} pending={registeringAsGuest} />
                </>
              )}
            </>
          )}

          {step === 'code' && (
            <CodeSection
              testId={testId}
              email={email}
              confirmCode={confirmCode}
              resendCode={resendCode}
              changeEmail={changeEmail}
              resendSecondsLeft={resendSecondsLeft}
              requesting={emailRequesting}
              validating={validating}
              finalizing={finalizing}
              initialEmail={initialEmail}
              onClose={handleClose}
            />
          )}

          {step === 'password' && (
            <PasswordSection
              testId={testId}
              email={email}
              confirmPassword={confirmPassword}
              requestPasswordReset={requestPasswordReset}
              changeEmail={changeEmail}
              validating={validatingPassword}
              finalizing={finalizing}
              requestingPasswordReset={requestingPasswordReset}
              initialEmail={initialEmail}
              onClose={handleClose}
            />
          )}

          {step === 'consents' && (
            <ConsentsSection
              testId={testId}
              expectedConsents={expectedConsents}
              acceptConsents={acceptConsents}
              finalizing={finalizing}
            />
          )}
        </div>
      </Modal>
      <AuthConversionConflictModal
        open={conversionConflictOpen}
        testId={testId.append('conversionConflict')}
        onConfirm={handleConfirmConflict}
        onCancel={handleCancelConflict}
        pending={requesting}
      />
    </>
  );
}
